import React from "react"
import { FormAutocomplete, FormTextField, useFormContext } from "@carrotfertility/carotene-core-x"
import { CountryCodes } from "#/utils/CountryCode"
import { AboutYou } from "#/types/carrotPlanTypes"
import { useIntl } from "react-intl"
import { Stack, useTheme } from "@carrotfertility/carotene-core"
import { useIntlSort } from "#/utils/hooks/useIntlSort"
import { MemberInternationalAddress } from "#/lib/carrot-api/types/Address"
import { addCountryOptionsEdgeCases } from "#/components/phone-number-collection/usePhoneCodeSelectOptions"

export type InternationalAddressFields = Omit<AboutYou["address"], "address1" | "address2" | "internationalCountry"> & {
  internationalCountry: { label: string; value: string }
}

export interface InternationalAddressProps {
  defaultValues?: MemberInternationalAddress
  stateValidation?: (country: { value: string } | undefined, state: string) => true | string
  zipValidation?: (country: { value: string } | undefined, zip: string) => true | string
}

export function RequestCardInternationalAddressForm(props: InternationalAddressProps) {
  const intl = useIntl()
  const { localeComparator } = useIntlSort()
  const theme = useTheme()
  const requiredText = intl.formatMessage({ defaultMessage: "Required" })
  const { watch } = useFormContext()
  const currentCountry = watch("internationalCountry")

  const countryOptions = CountryCodes.map((code) => ({
    label: intl.formatDisplayName(code, { type: "region" }),
    value: code
  }))
  const countryOptionsWithEdgeCases = addCountryOptionsEdgeCases(countryOptions, intl).sort((a, b) =>
    localeComparator(a.label, b.label)
  )

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      <FormTextField
        minRows={4}
        multiline
        defaultValue={props?.defaultValues?.internationalAddressLine}
        registerOptions={{
          required: requiredText
        }}
        inputProps={{ "data-testid": "about-you-internationalAddressLine" }}
        label={intl.formatMessage({ defaultMessage: "Street Address" })}
        name="internationalAddressLine"
        helperText={intl.formatMessage({ defaultMessage: "Include apartment, suite, unit, building, floor, etc." })}
      />
      <FormTextField
        defaultValue={props?.defaultValues?.city}
        registerOptions={{
          required: requiredText
        }}
        inputProps={{ "data-testid": "about-you-city" }}
        label={intl.formatMessage({ defaultMessage: "City" })}
        name="city"
      />
      <FormTextField
        defaultValue={props?.defaultValues?.state}
        registerOptions={{
          required: currentCountry?.value === "AU" || currentCountry?.value === "US" ? requiredText : false,
          validate: (value) => {
            if (props.stateValidation) {
              return props.stateValidation(currentCountry, value)
            }
            return true
          }
        }}
        inputProps={{ "data-testid": "about-you-state" }}
        label={intl.formatMessage({ defaultMessage: "State / Province / County / Region" })}
        name="state"
      />
      <FormAutocomplete
        fullWidth
        defaultValue={props?.defaultValues?.internationalCountry}
        controllerProps={{
          defaultValue: countryOptionsWithEdgeCases.find(
            ({ value }) => value === props?.defaultValues?.internationalCountry
          ),
          rules: { required: requiredText }
        }}
        data-testid={"about-you-internationalCountry"}
        options={countryOptionsWithEdgeCases}
        label={intl.formatMessage({ defaultMessage: "Country" })}
        name="internationalCountry"
      />
      <FormTextField
        defaultValue={props?.defaultValues?.zip}
        inputProps={{ "data-testid": "about-you-zip" }}
        label={intl.formatMessage({ defaultMessage: "Postal code" })}
        name="zip"
        helperText={intl.formatMessage({ defaultMessage: "Postal code is only required if your address has one." })}
        registerOptions={{
          validate: (value) => {
            if (props.zipValidation) {
              return props.zipValidation(currentCountry, value)
            }
            return true
          }
        }}
      />
    </Stack>
  )
}
